import React, { useEffect, useState, useRef } from 'react';
import {
  getProjectEmpreitas,
  createEmpreita,
  updateEmpreita,
  deleteEmpreita,
  uploadEmpreitaContract,
  getProviders,
  getCostCenters
} from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText, Upload, X } from 'lucide-react';

const STATUS_LABELS = {
  to_start: 'A Iniciar',
  in_progress: 'Em Andamento',
  completed: 'Concluída',
};

const STATUS_COLORS = {
  to_start: 'text-slate-600 bg-slate-100',
  in_progress: 'text-blue-600 bg-blue-50',
  completed: 'text-emerald-600 bg-emerald-50',
};

const emptyForm = () => ({
  nome: '',
  valor_total_cliente: '',
  status: 'to_start',
  data_inicio: '',
  data_fim: '',
  contrato_url: null,
  providers: [],
});

const EmpreitasManager = ({ projectId }) => {
  const [empreitas, setEmpreitas] = useState([]);
  const [providers, setProviders] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [providerLinkForm, setProviderLinkForm] = useState({ provider_id: '', codigo_servico: '', valor_custo_acordado: '' });
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchAll = async () => {
    if (!projectId) return;
    try {
      const [eRes, pRes, cRes] = await Promise.all([
        getProjectEmpreitas(projectId),
        getProviders(),
        getCostCenters(),
      ]);
      setEmpreitas(eRes.data);
      setProviders(pRes.data);
      setCostCenters(cRes.data);
    } catch (e) {
      toast.error('Erro ao carregar empreitas');
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setProviderLinkForm({ provider_id: '', codigo_servico: '', valor_custo_acordado: '' });
    setIsDialogOpen(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      nome: emp.nome || '',
      valor_total_cliente: emp.valor_total_cliente ?? '',
      status: emp.status || 'to_start',
      data_inicio: emp.data_inicio || '',
      data_fim: emp.data_fim || '',
      contrato_url: emp.contrato_url || null,
      providers: emp.providers || [],
    });
    setProviderLinkForm({ provider_id: '', codigo_servico: '', valor_custo_acordado: '' });
    setIsDialogOpen(true);
  };

  const addProviderLink = () => {
    if (!providerLinkForm.provider_id || !providerLinkForm.codigo_servico || !providerLinkForm.valor_custo_acordado) {
      toast.error('Preencha prestador, código do serviço e valor acordado');
      return;
    }
    // Prevent duplicate provider links
    if (form.providers.some(p => p.provider_id === providerLinkForm.provider_id)) {
      toast.error('Prestador já vinculado');
      return;
    }
    setForm({
      ...form,
      providers: [
        ...form.providers,
        {
          provider_id: providerLinkForm.provider_id,
          codigo_servico: providerLinkForm.codigo_servico,
          valor_custo_acordado: parseFloat(providerLinkForm.valor_custo_acordado),
        },
      ],
    });
    setProviderLinkForm({ provider_id: '', codigo_servico: '', valor_custo_acordado: '' });
  };

  const removeProviderLink = (providerId) => {
    setForm({ ...form, providers: form.providers.filter(p => p.provider_id !== providerId) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.valor_total_cliente) {
      toast.error('Preencha nome e valor total do cliente');
      return;
    }
    const payload = {
      project_id: projectId,
      nome: form.nome,
      valor_total_cliente: parseFloat(form.valor_total_cliente),
      status: form.status,
      data_inicio: form.data_inicio || null,
      data_fim: form.data_fim || null,
      contrato_url: form.contrato_url,
      providers: form.providers,
    };
    try {
      let savedId;
      if (editing) {
        await updateEmpreita(editing.id, payload);
        savedId = editing.id;
        toast.success('Empreita atualizada');
      } else {
        const resp = await createEmpreita(payload);
        savedId = resp.data.id;
        toast.success('Empreita criada');
      }
      // If a file is pending, upload contract now
      if (fileInputRef.current?.files?.[0]) {
        setUploading(true);
        await uploadEmpreitaContract(savedId, fileInputRef.current.files[0]);
        setUploading(false);
      }
      setIsDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar empreita');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta empreita? Esta ação não pode ser desfeita.')) return;
    try {
      await deleteEmpreita(id);
      toast.success('Empreita excluída');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const getProviderName = (pid) => providers.find(p => p.id === pid)?.name || pid;

  const allServices = costCenters.flatMap(cc => cc.services || []);
  const getServiceLabel = (code) => {
    const s = allServices.find(x => x.code === code);
    return s ? `${s.code} - ${s.description}` : code;
  };

  return (
    <div className="space-y-4" data-testid="empreitas-manager">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-sm text-slate-600">
          Gerencie as empreitas (sub-obras) deste projeto.
        </p>
        <Button size="sm" onClick={openNew} className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm w-full sm:w-auto" data-testid="new-empreita-button">
          <Plus className="h-4 w-4 mr-1" /> Nova Empreita
        </Button>
      </div>

      <div className="space-y-3">
        {empreitas.length === 0 && (
          <div className="text-center py-8 bg-slate-50 rounded-sm border border-dashed border-slate-300">
            <p className="text-sm text-slate-500">Nenhuma empreita cadastrada</p>
          </div>
        )}
        {empreitas.map((emp) => (
          <div key={emp.id} className="bg-white border border-slate-200 rounded-sm p-4" data-testid="empreita-card">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold text-slate-900 break-words">{emp.nome}</h4>
                  <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${STATUS_COLORS[emp.status] || ''}`}>
                    {STATUS_LABELS[emp.status] || emp.status}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  Valor Cliente: <strong>R$ {Number(emp.valor_total_cliente || 0).toFixed(2)}</strong>
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {emp.providers?.length || 0} prestador(es) vinculado(s)
                </p>
                {(emp.data_inicio || emp.data_fim) && (
                  <p className="text-xs text-slate-500 mt-1">
                    {emp.data_inicio ? `Início: ${emp.data_inicio}` : ''}
                    {emp.data_inicio && emp.data_fim ? ' · ' : ''}
                    {emp.data_fim ? `Fim: ${emp.data_fim}` : ''}
                  </p>
                )}
                {emp.contrato_url && (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${emp.contrato_url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-accent hover:underline mt-1"
                    data-testid="empreita-contract-link"
                  >
                    <FileText className="h-3 w-3" /> Ver contrato
                  </a>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => openEdit(emp)} className="rounded-sm" data-testid="edit-empreita-button">
                  <Edit className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(emp.id)} className="rounded-sm" data-testid="delete-empreita-button">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-secondary uppercase tracking-tight">
              {editing ? 'Editar Empreita' : 'Nova Empreita'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="empreita-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Nome *</Label>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required className="mt-1 rounded-sm" data-testid="empreita-nome-input" />
              </div>
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Valor Total Cliente *</Label>
                <Input type="number" step="0.01" value={form.valor_total_cliente} onChange={(e) => setForm({ ...form, valor_total_cliente: e.target.value })} required className="mt-1 rounded-sm" data-testid="empreita-valor-input" />
              </div>
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Status</Label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-sm border border-slate-200 bg-white text-sm" data-testid="empreita-status-select">
                  <option value="to_start">A Iniciar</option>
                  <option value="in_progress">Em Andamento</option>
                  <option value="completed">Concluída</option>
                </select>
              </div>
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Data Início</Label>
                <Input type="date" value={form.data_inicio} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Data Fim</Label>
                <Input type="date" value={form.data_fim} onChange={(e) => setForm({ ...form, data_fim: e.target.value })} className="mt-1 rounded-sm" />
              </div>
            </div>

            {/* Contract upload */}
            <div className="border-t border-slate-100 pt-4">
              <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500">Contrato (PDF ou imagem)</Label>
              <div className="flex items-center gap-2 mt-1">
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf" className="text-sm flex-1" data-testid="empreita-contract-input" />
                {form.contrato_url && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${form.contrato_url}`} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline">
                    Ver atual
                  </a>
                )}
              </div>
              {uploading && <p className="text-xs text-slate-500 mt-1">Enviando contrato…</p>}
            </div>

            {/* Provider links */}
            <div className="border-t border-slate-100 pt-4">
              <Label className="font-secondary uppercase text-xs tracking-wider text-slate-500 mb-2 block">Prestadores vinculados</Label>
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                <div className="sm:col-span-5">
                  <select value={providerLinkForm.provider_id} onChange={(e) => setProviderLinkForm({ ...providerLinkForm, provider_id: e.target.value })} className="w-full h-10 px-3 rounded-sm border border-slate-200 bg-white text-sm" data-testid="empreita-provider-select">
                    <option value="">Selecione prestador</option>
                    {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-4">
                  <select value={providerLinkForm.codigo_servico} onChange={(e) => setProviderLinkForm({ ...providerLinkForm, codigo_servico: e.target.value })} className="w-full h-10 px-3 rounded-sm border border-slate-200 bg-white text-sm" data-testid="empreita-service-select">
                    <option value="">Código do serviço</option>
                    {allServices.map(s => <option key={s.code} value={s.code}>{s.code} - {s.description}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Input type="number" step="0.01" placeholder="Valor custo" value={providerLinkForm.valor_custo_acordado} onChange={(e) => setProviderLinkForm({ ...providerLinkForm, valor_custo_acordado: e.target.value })} className="rounded-sm" data-testid="empreita-provider-value-input" />
                </div>
                <div className="sm:col-span-1">
                  <Button type="button" size="sm" onClick={addProviderLink} className="w-full rounded-sm" data-testid="empreita-add-provider-button">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-3 space-y-2">
                {form.providers.length === 0 && <p className="text-xs text-slate-500">Nenhum prestador vinculado</p>}
                {form.providers.map((link) => (
                  <div key={link.provider_id} className="flex items-center justify-between bg-slate-50 rounded-sm p-2 gap-2">
                    <div className="text-sm min-w-0 flex-1">
                      <p className="font-medium text-slate-900 break-words">{getProviderName(link.provider_id)}</p>
                      <p className="text-xs text-slate-500 break-words">
                        {getServiceLabel(link.codigo_servico)} · Custo: R$ {Number(link.valor_custo_acordado).toFixed(2)}
                      </p>
                    </div>
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeProviderLink(link.provider_id)} data-testid="empreita-remove-provider-button">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm" data-testid="submit-empreita-button">
                <Upload className="h-4 w-4 mr-1" /> {editing ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmpreitasManager;
